<template>
  <div class="pa-2 bg-grey-lighten-4 rounded-lg">
    <v-container class="pa-0 pb-2">
      <v-row>
        <v-col class="text-primary"> Assigned To </v-col>
      </v-row>
      <v-row v-if="!isValid">
        <v-col>
          <v-input :error-messages="errorMessages" :error="!isValid" :model-value="props.modelValue"></v-input>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="pa-0">
      <v-row>
        <v-col cols="12">
          <component
            :is="actualComponent"
            ref="autocompleteRef"
            v-model="selectedUsers"
            v-model:search="searchTerm"
            :items="autocompleteItems"
            :loading="loading"
            :label="props.label"
            :disabled="props.disabled"
            multiple
            chips
            closable-chips
            :menu-props="{ maxWidth: '100%' }"
            placeholder="Search for a user..."
            @update:model-value="onSelectionChange"
            @update:search="onSearchUpdate">
            <template #chip="{ props: chipProps, item }">
              <v-chip v-bind="chipProps" :closable="!props.disabled" @click:close="removeUser(item.value)">
                {{ item.value }}
              </v-chip>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :title="item.title" />
            </template>
          </component>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { watch, ref, computed, nextTick } from "vue";
import { VAutocomplete } from "vuetify/components";
import debounce from "lodash.debounce";

const props = defineProps({
  remoteSearch: {
    type: Function,
    default: () => () => Promise.resolve([]),
  },
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  component: {
    type: [String, Object],
    default: null,
  },
  modelValue: {
    type: Array,
    required: true,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  // childRules not used - selections are from dropdown only, so always valid
  // Declared as no-op prop to suppress Vue warnings when passed via vBind
  childRules: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

// Use the provided component or default to v-autocomplete
const actualComponent = computed(() => props.component || VAutocomplete);

const emit = defineEmits(["update:modelValue"]);

const autocompleteRef = ref(null);
const searchTerm = ref("");
const loading = ref(false);
const searchResults = ref([]);
const isValid = ref(true);
const errorMessages = ref([]);

// Computed selected users that syncs with modelValue
// Defensive: ensure we always return an array (v-combobox may pass non-array values)
const selectedUsers = computed({
  get: () => (Array.isArray(props.modelValue) ? props.modelValue : []),
  set: (value) => emit("update:modelValue", Array.isArray(value) ? value : []),
});

// Combine search results with static items, excluding already selected users
const autocompleteItems = computed(() => {
  const staticItems = props.items.map((item) => {
    if (typeof item === "string") {
      return { title: item, value: item };
    }
    return item;
  });

  // Merge static items and search results, avoiding duplicates
  const allItems = [...staticItems];
  for (const result of searchResults.value) {
    const exists = allItems.some((item) => item.value === result.value);
    if (!exists) {
      allItems.push(result);
    }
  }

  // Filter out already selected users from dropdown
  // Defensive: handle non-array modelValue (v-combobox may pass non-array values)
  const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
  // Handle both object and string users in modelValue
  const selectedValues = currentValue.map((user) => (typeof user === "object" && user !== null ? user.value : user));
  return allItems.filter((item) => !selectedValues.includes(item.value));
});

// Debounced search function
const debouncedSearch = debounce(async (value) => {
  if (!value || !props.remoteSearch) {
    loading.value = false;
    searchResults.value = [];
    return;
  }

  try {
    const results = await props.remoteSearch(value);
    searchResults.value = results || [];
  } catch {
    searchResults.value = [];
  } finally {
    loading.value = false;
  }
}, 300);

const onSearchUpdate = (value) => {
  loading.value = true;
  debouncedSearch(value);
};

const onSelectionChange = (newSelection) => {
  emit("update:modelValue", newSelection);
  // Clear search after selection to allow searching for another user
  nextTick(() => {
    searchTerm.value = "";
    searchResults.value = [];
  });
};

const removeUser = (userValue) => {
  // Defensive: handle non-array modelValue (v-combobox may pass non-array values)
  const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
  // Handle both object and string users - v-combobox may store objects while userValue is the string
  const updatedUsers = currentValue.filter((user) => {
    const currentUserValue = typeof user === "object" && user !== null ? user.value : user;
    return currentUserValue !== userValue;
  });
  emit("update:modelValue", updatedUsers);
};

const validate = () => {
  errorMessages.value = [];
  // Defensive: handle non-array modelValue (v-combobox may pass non-array values)
  const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
  for (const rule of props.rules) {
    const result = rule(currentValue);
    if (result !== true) {
      errorMessages.value.push(result);
      isValid.value = false;
      return false;
    }
  }
  isValid.value = true;
  return true;
};

watch(() => props.modelValue, validate, { deep: true });
watch(() => props.rules, validate, { immediate: true });

defineExpose({
  validate,
  isValid,
  errorMessages,
});
</script>
